import { FREE_CONSTANTS } from '@/constants';
import {
    defaultAddress,
    STARTER_2024_PACKAGE,
    STARTER_MAU_OPTIONS,
    STARTER_2024_MAU_OPTIONS,
    STARTER_2024_PRICE_PER_MAU_OPTIONS,
    STARTER_PACKAGE,
} from '@/free/free.constants.js';
import { fetchSetting } from '@/stores/subscription.utils.js';
import { http } from '@pendo/http';

export function showCelebrationModalOnPendoReady (afterGuideShownCallback) {
    const ready = pendo.isReady();

    if (ready) {
        triggerCelebrationModal(afterGuideShownCallback);
    } else {
        pendo.events.ready(() => {
            triggerCelebrationModal(afterGuideShownCallback);
        });
    }
}

export function showCelebrationModal (afterGuideShownCallback) {
    if (showCelebrationModal.hasRun) return;

    const hasShown = pendo.showGuideByName('[do not delete] Snippet Installed Celebration');

    if (hasShown) {
        afterGuideShownCallback();
    }

    showCelebrationModal.hasRun = true;
}

export async function triggerCelebrationModal (afterGuideShownCallback) {
    if (window.location.pathname.indexOf('/onboarding') > -1) return;

    const seenCelebrationModal = (await fetchSetting('seenCelebrationModal')) === 'true';

    if (!seenCelebrationModal) {
        const guide = pendo.findGuideByName('[do not delete] Snippet Installed Celebration');

        if (!guide) {
            pendo.events.guidesLoaded(() => {
                showCelebrationModal(afterGuideShownCallback);
            });
        } else {
            showCelebrationModal(afterGuideShownCallback);
        }
    }
}

export function getSampleDataSubName () {
    if (['prod-eu', 'eu-test', 'eu-dev'].includes(window.appEnvId)) {
        return FREE_CONSTANTS.SampleDataSubNameEU;
    }

    return FREE_CONSTANTS.SampleDataSubNameUS;
}

const MILLISECONDS_IN_A_DAY = 1000 * 60 * 60 * 24;

export function getDaysRemainingInTrial (endDateTimestamp, isInAutoProvisionedTrial) {
    if (endDateTimestamp < Date.now()) {
        return 0;
    }

    if (isInAutoProvisionedTrial) {
        return Math.ceil((endDateTimestamp - Date.now()) / MILLISECONDS_IN_A_DAY);
    }

    // Since both Date.now() and the end date are in UTC we don't need to go through moment.js
    return Math.floor((endDateTimestamp - Date.now()) / MILLISECONDS_IN_A_DAY);
}

export function formatAddress (addressObject, countryName) {
    const { line1, line2, city, state, zip } = addressObject;

    const printedLine1 = line1 ? `${line1}, ` : '';
    const printedLine2 = line2 ? `${line2}, ` : '';
    const printedCity = city ? `${city}, ` : '';
    let printedState = state ? `${state}` : '';
    if (printedState && !zip) {
        printedState += ', ';
    }
    const printedZip = zip ? ` ${zip}, ` : '';
    const printedCountry = `${countryName}`;

    return `${printedLine1}${printedLine2}${printedCity}${printedState}${printedZip}${printedCountry}`;
}

export function formatCurrency (number, currency = 'USD') {
    const locale = navigator.language;

    return number.toLocaleString(locale, { style: 'currency', currency });
}

export async function validateCoupon (promoCode) {
    return http.post('/api/s/_SID_/billing/coupon/verify', { promoCode });
}

export function getCustomerDetailsInitialState () {
    return {
        firstName: '',
        lastName: '',
        companyName: '',
        email: '',
        phone: '',
        website: '',
        companyAddress: Object.assign({}, defaultAddress),
        billingAddress: Object.assign({}, defaultAddress),
    };
}

export function getValidMauTiers ({
    mauCount,
    mauLimit,
    isOpsUser,
    isSuperUser,
    isUpgrading,
    isStarterPackage,
    purchasableSelfServicePlan,
}) {
    const isStarter2024Package = purchasableSelfServicePlan === STARTER_2024_PACKAGE.planId;
    const mauTiers = isStarter2024Package ? STARTER_2024_MAU_OPTIONS : STARTER_MAU_OPTIONS;

    if (isOpsUser || isSuperUser) {
        if (isUpgrading) {
            return [{ count: 2, label: '2' }, ...mauTiers];
        }

        return [{ count: 1, label: '1' }, ...mauTiers];
    }

    if (isStarterPackage && isUpgrading) {
        return mauTiers.filter((tier) => tier.count >= mauLimit);
    }

    return mauTiers.filter((tier) => tier.count > mauCount);
}

export function getValidPricePerMau ({ selectedMau, paymentPeriod, purchasableSelfServicePlan }) {
    if (purchasableSelfServicePlan === STARTER_2024_PACKAGE.planId) {
        return STARTER_2024_PRICE_PER_MAU_OPTIONS[paymentPeriod][selectedMau];
    }

    return STARTER_PACKAGE.options[paymentPeriod].pricePerMau;
}

export function calculateDiscount ({ planId, selectedMau }) {
    if (planId === STARTER_2024_PACKAGE.planId) {
        const starter2024AnnualPrice = selectedMau * STARTER_2024_PRICE_PER_MAU_OPTIONS.annual[selectedMau];
        const starter2024QuarterlyPrice = selectedMau * STARTER_2024_PRICE_PER_MAU_OPTIONS.quarterly[selectedMau] * 4;

        return Math.floor(((starter2024QuarterlyPrice - starter2024AnnualPrice) / starter2024QuarterlyPrice) * 100);
    }

    const starterAnnualPrice = selectedMau * STARTER_PACKAGE.options.annual.pricePerMau;
    const starterQuarterlyPrice = selectedMau * STARTER_PACKAGE.options.quarterly.pricePerMau * 4;

    return Math.floor(((starterQuarterlyPrice - starterAnnualPrice) / starterQuarterlyPrice) * 100);
}

export function getPricePerMauLabel ({ selectedMauTierAboveStarterLimit, quarterlyEnabled, pricePerMau }) {
    if (selectedMauTierAboveStarterLimit) {
        return '';
    }

    if (quarterlyEnabled) {
        return `$${(pricePerMau * 4).toFixed(2)} per MAU`;
    }

    return `$${(Math.round(pricePerMau * 100) / 100).toFixed(2)} per MAU`;
}
